/**
 * React Minified Error Decoder
 *
 * In production, React uses minified error messages like:
 * "Minified React error #418; visit https://react.dev/errors/418?args[]=HTML&args[]= for the full message"
 *
 * This module decodes these errors into human-readable messages for better debugging.
 *
 * @see https://react.dev/errors
 */

// =============================================================================
// REACT ERROR CODE MAPPINGS
// These are the most common React errors with their full message templates
// The %s placeholders are replaced with the args from the error URL
// =============================================================================

const REACT_ERROR_MESSAGES: Record<number, string> = {
  // Hydration Errors (most common in Next.js)
  418: 'Hydration failed because the initial UI does not match what was rendered on the server. Expected: "%s", Found: "%s"',
  419: 'The server could not finish this Suspense boundary, likely due to an error during server rendering.',
  420: 'Hydration failed because the initial UI does not match what was rendered on the server.',
  421: 'This Suspense boundary received an update before it finished hydrating.',
  422: 'There was an error while hydrating this Suspense boundary. Switched to client rendering.',
  423: 'There was an error while hydrating. Because the error happened outside of a Suspense boundary, the entire root will switch to client rendering.',
  424: 'Text content does not match server-rendered HTML. Expected: "%s", Found: "%s"',
  425: 'Text content does not match server-rendered HTML.',
  426: 'A component suspended while responding to synchronous input.',

  // Component Errors
  31: 'Objects are not valid as a React child (found: %s). If you meant to render a collection of children, use an array instead.',
  130: 'Element type is invalid: expected a string or a class/function but got: %s.',
  152: 'Nothing was returned from render. This usually means a return statement is missing.',
  301: 'Maximum update depth exceeded. This can happen when a component calls setState inside useEffect, but useEffect either doesn\'t have a dependency array, or one of the dependencies changes on every render.',

  // Hooks Errors
  300: 'Invalid hook call. Hooks can only be called inside of the body of a function component.',
  310: 'Rendered fewer hooks than expected. This may be caused by an accidental early return statement.',
  311: 'Rendered more hooks than during the previous render.',

  // Context/Provider Errors
  38: 'You cannot use context outside of a component. Please move the context consumer or provider into a component.',

  // Suspense/Streaming Errors (426 defined above in hydration section)
  427: 'An update was made to a component that is already unmounted. This is a no-op, but it indicates a memory leak in your application.',

  // Server Component Errors
  428: 'This error is likely caused by a bug in React. Please file an issue.',
  429: 'The server encountered an error before the response could be generated.',

  // Form/Action Errors
  430: 'An action was called multiple times. Actions can only be called once per render.',
};

// =============================================================================
// DECODER INTERFACE
// =============================================================================

export interface DecodedReactError {
  /** Whether this is a minified React error */
  isReactError: boolean;
  /** The error code (e.g., 418) */
  errorCode: number | null;
  /** The arguments from the error URL */
  args: string[];
  /** The full decoded message */
  fullMessage: string;
  /** The original error message */
  originalMessage: string;
  /** Link to React docs for this error */
  docsUrl: string | null;
  /** Quick summary of what this error type means */
  errorCategory: string | null;
}

// =============================================================================
// DECODER FUNCTION
// =============================================================================

/**
 * Decode a React minified error message into a human-readable format.
 *
 * @param errorMessage - The error message to decode
 * @returns Decoded error information
 *
 * @example
 * ```typescript
 * const decoded = decodeReactError(
 *   'Minified React error #418; visit https://react.dev/errors/418?args[]=HTML&args[]= for the full message'
 * );
 * // Returns:
 * // {
 * //   isReactError: true,
 * //   errorCode: 418,
 * //   args: ['HTML', ''],
 * //   fullMessage: 'Hydration failed because the initial UI does not match what was rendered on the server. Expected: "HTML", Found: ""',
 * //   originalMessage: '...',
 * //   docsUrl: 'https://react.dev/errors/418',
 * //   errorCategory: 'Hydration Mismatch'
 * // }
 * ```
 */
export function decodeReactError(errorMessage: string): DecodedReactError {
  const result: DecodedReactError = {
    isReactError: false,
    errorCode: null,
    args: [],
    fullMessage: errorMessage,
    originalMessage: errorMessage,
    docsUrl: null,
    errorCategory: null,
  };

  // Check if this is a minified React error
  const minifiedPattern = /Minified React error #(\d+)/i;
  const match = errorMessage.match(minifiedPattern);

  if (!match) {
    return result;
  }

  result.isReactError = true;
  result.errorCode = parseInt(match[1], 10);

  // Extract args from the URL in the error message
  const urlPattern = /https:\/\/react\.dev\/errors\/\d+\?([^"'\s]+)/i;
  const urlMatch = errorMessage.match(urlPattern);

  if (urlMatch) {
    const queryString = urlMatch[1];
    // Parse args[]=value patterns
    const argsPattern = /args\[\]=([^&]*)/g;
    let argMatch;
    while ((argMatch = argsPattern.exec(queryString)) !== null) {
      // Decode URL-encoded values
      const decodedArg = decodeURIComponent(argMatch[1] || '');
      result.args.push(decodedArg);
    }
  }

  // Build docs URL
  result.docsUrl = `https://react.dev/errors/${result.errorCode}`;

  // Get the error template and fill in args
  const template = REACT_ERROR_MESSAGES[result.errorCode];
  if (template) {
    result.fullMessage = formatErrorMessage(template, result.args);
  } else {
    // Unknown error code - provide helpful context anyway
    result.fullMessage = `React Error #${result.errorCode}: ${result.args.length > 0 ? `Arguments: [${result.args.join(', ')}]` : 'No arguments'}\n\nSee ${result.docsUrl} for details.`;
  }

  // Categorize the error
  result.errorCategory = categorizeError(result.errorCode);

  return result;
}

/**
 * Format an error message template with arguments.
 * Replaces %s placeholders with the provided args.
 */
function formatErrorMessage(template: string, args: string[]): string {
  let result = template;
  let argIndex = 0;

  // Replace %s placeholders with args
  result = result.replace(/%s/g, () => {
    if (argIndex < args.length) {
      const arg = args[argIndex];
      argIndex++;
      // Show empty strings clearly
      return arg === '' ? '(empty string)' : arg;
    }
    return '(missing arg)';
  });

  // If there are extra args, append them
  if (argIndex < args.length) {
    const extraArgs = args.slice(argIndex).map(a => a === '' ? '(empty)' : a);
    result += `\n\nAdditional arguments: [${extraArgs.join(', ')}]`;
  }

  return result;
}

/**
 * Categorize an error code into a human-readable category.
 */
function categorizeError(errorCode: number): string {
  if (errorCode >= 418 && errorCode <= 426) {
    return 'Hydration Mismatch';
  }
  if (errorCode >= 300 && errorCode <= 320) {
    return 'React Hooks Error';
  }
  if (errorCode >= 427 && errorCode <= 430) {
    return 'Server/Streaming Error';
  }
  if (errorCode === 31 || errorCode === 130 || errorCode === 152) {
    return 'Invalid Render Output';
  }
  if (errorCode === 301) {
    return 'Infinite Loop';
  }
  return 'React Internal Error';
}

/**
 * Check if an error message is a minified React error.
 * Quick check without full decoding.
 */
export function isMinifiedReactError(errorMessage: string): boolean {
  return /Minified React error #\d+/i.test(errorMessage);
}

/**
 * Get a quick diagnostic summary for common React errors.
 * Useful for showing in the error report UI.
 */
export function getQuickDiagnostic(errorCode: number): string | null {
  const diagnostics: Record<number, string> = {
    418: '🔍 HYDRATION MISMATCH: Server HTML differs from client render. Common causes:\n• Date/time rendered without suppressHydrationWarning\n• Browser-only APIs used during SSR (window, localStorage)\n• Conditional rendering based on client-only state\n• Invalid HTML nesting (e.g., <p> inside <p>, <a> inside <a>)\n• Browser extensions modifying DOM before hydration',

    419: '⚠️ SUSPENSE BOUNDARY ERROR: Server couldn\'t complete a Suspense boundary. Check server logs for the underlying error.',

    420: '🔍 HYDRATION MISMATCH: General mismatch between server and client. Enable React DevTools to see which element differs.',

    424: '📝 TEXT MISMATCH: Text content differs between server and client. Often caused by:\n• Formatting dates/numbers differently\n• Dynamic content like "X minutes ago"\n• Random values or UUIDs generated during render',

    425: '📝 TEXT MISMATCH: Server-rendered text doesn\'t match client. Check for dynamic content.',

    300: '⚠️ INVALID HOOK CALL: Hooks called outside component or in wrong order. Check:\n• Not calling hooks inside conditions/loops\n• Not calling hooks from regular functions\n• Only one React version installed',

    301: '🔄 INFINITE LOOP: Component keeps re-rendering. Check:\n• useEffect dependencies\n• setState calls that trigger re-renders\n• Object/array dependencies not memoized',

    31: '❌ INVALID CHILD: Trying to render an object as a child. Check:\n• Not rendering plain objects or Promises\n• Using .map() correctly on arrays\n• Date objects need .toString() or formatting',
  };

  return diagnostics[errorCode] || null;
}
